import { useCallback, useEffect, useRef } from 'react';

const KEYBOARD_STEP_PX = 16;

// Accessible drag handle between workspace columns (508: keyboard-resizable
// with real separator semantics). Mouse drag reports absolute deltas from the
// drag start; arrow keys nudge in fixed steps; double-click resets.
export function WorkspaceSplitter({
  label,
  testId,
  value,
  min,
  max,
  onResize,
  onReset,
  invert = false,
}) {
  const dragRef = useRef(null);
  // Callers pass fresh inline callbacks on every render. The document-level
  // handlers must stay IDENTITY-STABLE across those re-renders — an unstable
  // handler chain would tear down the active drag's listeners in the effect
  // cleanup after the very first pointermove re-render, freezing the drag.
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;
  const invertRef = useRef(invert);
  invertRef.current = invert;

  const onPointerMove = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag) return;
    const rawDelta = event.clientX - drag.startX;
    onResizeRef.current(drag.startValue + (invertRef.current ? -rawDelta : rawDelta));
  }, []);

  const endDrag = useCallback(() => {
    dragRef.current = null;
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', endDrag);
    document.removeEventListener('pointercancel', endDrag);
    window.removeEventListener('blur', endDrag);
    document.body.classList.remove('fbws-resizing');
  }, [onPointerMove]);

  // A drag interrupted by unmount (route change), pointer cancellation, or
  // the window losing focus must never leave document listeners or the
  // col-resize body class behind. endDrag is identity-stable, so this cleanup
  // runs only on unmount.
  useEffect(() => endDrag, [endDrag]);

  return (
    <div
      role="separator"
      tabIndex={0}
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={min}
      aria-valuemax={max}
      data-testid={testId}
      className="fbws-splitter"
      title={`${label} — drag or use arrow keys; double-click to reset`}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        dragRef.current = { startX: event.clientX, startValue: value };
        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', endDrag);
        document.addEventListener('pointercancel', endDrag);
        window.addEventListener('blur', endDrag);
        document.body.classList.add('fbws-resizing');
      }}
      onDoubleClick={onReset}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          onResize(value + (invert ? -direction : direction) * KEYBOARD_STEP_PX);
        } else if (event.key === 'Home') {
          event.preventDefault();
          onResize(min);
        } else if (event.key === 'End') {
          event.preventDefault();
          onResize(max);
        } else if (event.key === 'Enter') {
          event.preventDefault();
          onReset?.();
        }
      }}
    >
      <span className="fbws-splitter-grip" aria-hidden />
    </div>
  );
}
